import { useMemo, useState, useCallback, useContext, useEffect, useRef } from 'react';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import Swal from 'sweetalert2';
import { isIpad, isDesktop } from '../../../utils/ScreenSizeHelper';
import { GlobalStateDataContext } from '../../../context-api/data-provider/GlobalStateDataProvider';
import { KitchenOperationUpdateContext } from '../../../context-api/data-provider/UpdateOperationDataProvider';
import { ProductDataContext } from '../../../context-api/data-provider/ProductDataProvider';
import {
  MAX_SEARCH_KEYWORDS,
  MAX_PICKUP_FREE_ITEMS,
  STORE_FRONT_IMAGE_FIELDS,
  YOUR_PICK_IMAGE_SECTION,
} from './storeSettingsConstants';
import {
  deepClone,
  getEmptyFormState,
  mapLoadResponseToForm,
  isTabDirty,
  buildTabUpdatePayload,
  mergeBaselineForTab,
} from './storeSettingsForm';

/** All editable `store_front_images` slots (singles + the "Your Pick" trio), for upload handling. */
const ALL_STORE_FRONT_IMAGE_FIELDS = [
  ...YOUR_PICK_IMAGE_SECTION.fields,
  ...STORE_FRONT_IMAGE_FIELDS,
];

/** First human-readable message from kitchen update API `errors` array (or empty string). */
function kitchenUpdateErrorsMessage(result) {
  const list = result?.errors;
  if (!Array.isArray(list) || list.length === 0) return '';
  const parts = list
    .map((e) => {
      if (e == null) return '';
      if (typeof e === 'string') return e;
      return e.errorMessage || e.message || e.Message || e.customMessage || '';
    })
    .filter(Boolean);
  return parts.join(' ');
}

const isPositiveNumber = (value) => Number(value) > 0;
const MAX_DELIVERY_MIN_SPEND = 999;
const MAX_DELIVERY_KITCHEN_PERCENT = 100;
const MAX_STORE_PRICE_OR_AMOUNT = 999;

/**
 * Sanitizes decimal input while typing: keeps digits and a single `.` (max 2 decimal places),
 * without collapsing intermediate states like a trailing "9." — reformatting via Number()/String()
 * on every keystroke would strip the dot and make decimals impossible to type.
 */
const sanitizeBoundedDecimal = (value, maxValue) => {
  let raw = String(value ?? '').replace(/[^0-9.]/g, '');

  const firstDot = raw.indexOf('.');
  if (firstDot !== -1) {
    raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
  }

  const [intPart, decPart] = raw.split('.');
  if (decPart != null && decPart.length > 2) {
    raw = `${intPart}.${decPart.slice(0, 2)}`;
  }

  if (raw === '' || raw === '.') return raw;

  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > maxValue) return String(maxValue);

  return raw;
};

const JS_BLOCKED_KEYWORDS =
  /\b(?:function|const|let|var|class|import|export|return|if|else|for|while|switch|case|default|new|this|eval|script)\b/gi;

const sanitizeStoreText = (value) =>
  String(value ?? '')
    .replace(JS_BLOCKED_KEYWORDS, ' ')
    .replace(/[^a-zA-Z0-9 ]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trimStart();

const sanitizeStoreNameText = (value) =>
  String(value ?? '')
    .replace(JS_BLOCKED_KEYWORDS, ' ')
    .replace(/[^a-zA-Z ]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trimStart();

export const capitalizeWords = (value) =>
  String(value ?? '').replace(/\b([a-z])/g, (match) => match.toUpperCase());

const hasAtLeastOneSelectableFreeItem = (items) =>
  (Array.isArray(items) ? items : []).slice(1).some((item) => {
    const name = String(item?.name ?? '')
      .trim()
      .toLowerCase();
    const size = String(item?.size ?? '')
      .trim()
      .toLowerCase();
    return name !== '' && size !== '' && !(name === 'none' && size === 'none');
  });

export function useStoreSettings() {
  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);
  const { onKitchenOperationUpdateAPI, kitchenUpdateAPIResponse } = useContext(
    KitchenOperationUpdateContext
  );
  const { onUploadImageCludinary, uploadImage } = useContext(ProductDataContext);

  const [form, setForm] = useState(getEmptyFormState);
  const [baseline, setBaseline] = useState(null);
  const [storeImageFile, setStoreImageFile] = useState(null);
  const [storeImagePreviewUrl, setStoreImagePreviewUrl] = useState('');
  const [storeFrontImageFiles, setStoreFrontImageFiles] = useState({});
  const [storeFrontImagePreviewUrls, setStoreFrontImagePreviewUrls] = useState({});
  const [saveAcknowledged, setSaveAcknowledged] = useState(false);
  /** Prevents overlapping saves: context `loading` flips after async setState, so double-clicks could fire two PUTs. */
  const saveInFlightRef = useRef(false);

  const loadDataRoot = onLoadInfoResponse?.data?.data?.data;
  const loadInFlight = onLoadInfoResponse?.loading === true;
  const kitchenName = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.kitchenName;

  useEffect(() => {
    if (loadInFlight || !loadDataRoot) return;
    const mapped = mapLoadResponseToForm(loadDataRoot);
    if (!mapped) return;
    setForm(mapped);
    setBaseline(deepClone(mapped));
    setStoreImageFile(null);
    setStoreImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setStoreFrontImageFiles({});
    setStoreFrontImagePreviewUrls((prev) => {
      Object.values(prev).forEach((url) => url && URL.revokeObjectURL(url));
      return {};
    });
  }, [loadInFlight, loadDataRoot]);

  const sectionMeta = useMemo(
    () => [
      {
        id: '1',
        title: 'Store profile',
        subtitle: 'Set your public display name and upload your store logo',
        Icon: StorefrontOutlinedIcon,
      },
      {
        id: '2',
        title: 'Operating hours',
        subtitle:
          'Your operating hours control when your store is shown as open or closed for orders.',
        Icon: AccessTimeOutlinedIcon,
      },
      {
        id: '3',
        title: 'Menu availability',
        subtitle: 'Control visibility of Combo and Regular menus.',
        Icon: MenuBookOutlinedIcon,
      },
      {
        id: '4',
        title: 'Delivery Discount',
        subtitle:
          'Offer a delivery discount when customers spend a certain amount to boost order value',
        Icon: HandshakeOutlinedIcon,
      },
      {
        id: '5',
        title: 'Pickup Promotion',
        subtitle:
          'Offer a free item on pickup orders to help customers save on delivery and enjoy an extra reward',
        Icon: LocalOfferOutlinedIcon,
      },
      {
        id: '6',
        title: 'Delivery & Pickup options',
        subtitle: 'Manage which ordering options are available to customers (Delivery and Pickup).',
        Icon: LocalShippingOutlinedIcon,
      },
      {
        id: '7',
        title: 'Store Search Keywords',
        subtitle: 'Enter keywords and popular product names to help customers find your store.',
        Icon: SearchOutlinedIcon,
      },
      {
        id: '8',
        title: 'Filters & Religion',
        subtitle: 'Choose the food and dietary/religious filter categories customers can use to find your store.',
        Icon: FilterAltOutlinedIcon,
      },
      {
        id: '9',
        title: 'Coupons',
        subtitle: 'Create and manage coupon codes customers can apply at checkout.',
        Icon: ConfirmationNumberOutlinedIcon,
      },
    ],
    []
  );

  const [selectedId, setSelectedId] = useState('1');

  useEffect(() => {
    setSaveAcknowledged(false);
  }, [selectedId]);

  const allowLayout = isIpad || isDesktop;
  const MAX_WIDTH = isIpad ? 800 : 1200;

  const handleTabUpdate = useCallback(
    async (tabId) => {
      if (!baseline) return;
      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;

      try {
        let workingForm = form;

        if (tabId === '1' && storeImageFile) {
          if (!kitchenName) {
            Swal.fire({
              icon: 'error',
              title: 'Cannot upload image',
              text: 'Kitchen name is missing.',
            });
            return;
          }
          const formData = new FormData();
          formData.append('file', storeImageFile);
          formData.append('fileName', `${kitchenName}_store_${Date.now()}`);
          formData.append('kitchen_name', kitchenName);
          const uploadRes = await onUploadImageCludinary(formData);
          if (!uploadRes?.data?.success) {
            Swal.fire({
              icon: 'error',
              title: 'Image upload failed',
              text:
                uploadRes?.data?.CustomMessage ||
                uploadRes?.errorMessage ||
                'Could not upload the store image.',
            });
            return;
          }
          const secureUrl = uploadRes?.data?.secureUrl ?? '';
          workingForm = { ...form, display_image: secureUrl };
          setForm(workingForm);
          setStoreImageFile(null);
          if (storeImagePreviewUrl) URL.revokeObjectURL(storeImagePreviewUrl);
          setStoreImagePreviewUrl('');
        }

        const pendingStoreFrontImageFields = ALL_STORE_FRONT_IMAGE_FIELDS.filter(
          (field) => storeFrontImageFiles[field.key]
        );

        if (tabId === '1' && pendingStoreFrontImageFields.length > 0) {
          if (!kitchenName) {
            Swal.fire({
              icon: 'error',
              title: 'Cannot upload image',
              text: 'Kitchen name is missing.',
            });
            return;
          }

          const nextStoreFrontImages = { ...workingForm.store_front_images };
          for (const field of pendingStoreFrontImageFields) {
            const formData = new FormData();
            formData.append('file', storeFrontImageFiles[field.key]);
            formData.append('fileName', `${kitchenName}_${field.key}_${Date.now()}`);
            formData.append('kitchen_name', kitchenName);
            const uploadRes = await onUploadImageCludinary(formData);
            if (!uploadRes?.data?.success) {
              Swal.fire({
                icon: 'error',
                title: 'Image upload failed',
                text:
                  uploadRes?.data?.CustomMessage ||
                  uploadRes?.errorMessage ||
                  `Could not upload ${field.label}.`,
              });
              return;
            }
            nextStoreFrontImages[field.key] = uploadRes?.data?.secureUrl ?? '';
          }
          workingForm = { ...workingForm, store_front_images: nextStoreFrontImages };
          setForm(workingForm);
          setStoreFrontImageFiles({});
          setStoreFrontImagePreviewUrls((prev) => {
            Object.values(prev).forEach((url) => url && URL.revokeObjectURL(url));
            return {};
          });
        }

        if (tabId === '3' && workingForm.comboMenuAvailable === true) {
          if (!isPositiveNumber(workingForm.comboMenuPrice)) {
            Swal.fire({
              icon: 'error',
              title: 'Cannot save',
              text: 'Enter a combo menu price greater than 0.',
            });
            return;
          }
          if (!workingForm.comboMenuLabelName) {
            Swal.fire({
              icon: 'error',
              title: 'Cannot save',
              text: 'Select a combo menu label.',
            });
            return;
          }
        }

        if (tabId === '5' && workingForm.pickup_order_promotion?.pickup_promo_available === true) {
          const promo = workingForm.pickup_order_promotion;
          if (!isPositiveNumber(promo.minimumAmountSpend)) {
            Swal.fire({
              icon: 'error',
              title: 'Cannot save',
              text: 'Enter a minimum spend amount greater than 0 for pickup promotion.',
            });
            return;
          }
          if (!hasAtLeastOneSelectableFreeItem(promo.pickup_free_items)) {
            Swal.fire({
              icon: 'error',
              title: 'Cannot save',
              text: 'Add at least one free item (other than none/none) for pickup promotion.',
            });
            return;
          }
        }

        if (tabId === '4') {
          const fee = workingForm.delivery_agreement_fees || {};
          const minSpend = String(fee.minimumAmountSpend ?? '').trim();
          const kitchenPct = String(fee.percentageToPay_byKitchen ?? '').trim();
          if (!minSpend || !kitchenPct) {
            Swal.fire({
              icon: 'error',
              title: 'Cannot save',
              text: 'Please enter both Minimum order amount and Kitchen pays (% of fee).',
            });
            return;
          }
        }

        if (!isTabDirty(tabId, workingForm, baseline, { pendingStoreImageFile: null })) return;

        const patch = buildTabUpdatePayload(tabId, workingForm, baseline);
        if (Object.keys(patch).length === 0) return;

        const kitchen_name = kitchenName || workingForm.kitchen_name;
        if (!kitchen_name || !String(kitchen_name).trim()) {
          Swal.fire({
            icon: 'error',
            title: 'Cannot save',
            text: 'Kitchen name is missing. Try reloading the page.',
          });
          return;
        }

        const operationPayload = { ...patch, kitchen_name: String(kitchen_name).trim() };
        const formSnap = deepClone(workingForm);
        const result = await onKitchenOperationUpdateAPI(operationPayload);
        const ok = result?.success === true;
        const errorsMsg = kitchenUpdateErrorsMessage(result);
        const msg = ok
          ? result?.customMessage ||
            result?.CustomMessage ||
            result?.message ||
            'Store settings updated.'
          : errorsMsg ||
            result?.customMessage ||
            result?.CustomMessage ||
            result?.message ||
            result?.errorMessage ||
            'Update failed.';

        Swal.fire({
          icon: ok ? 'success' : 'error',
          title: ok ? 'Saved' : 'Update failed',
          text: msg,
        });

        if (ok) {
          setBaseline((b) => mergeBaselineForTab(b, tabId, formSnap));
          setSaveAcknowledged(false);
        }
      } finally {
        saveInFlightRef.current = false;
      }
    },
    [
      baseline,
      form,
      kitchenName,
      onKitchenOperationUpdateAPI,
      onUploadImageCludinary,
      storeImageFile,
      storeImagePreviewUrl,
      storeFrontImageFiles,
    ]
  );

  const updateField = useCallback((path, value) => {
    setForm((prev) => {
      if (path === 'kitchen_name') return { ...prev, kitchen_name: value };
      if (path === 'phone_no') return { ...prev, phone_no: value };
      if (path === 'display_name')
        return { ...prev, display_name: capitalizeWords(sanitizeStoreNameText(value)) };
      if (path === 'display_image') return { ...prev, display_image: value };
      if (path === 'operation_start_time') return { ...prev, operation_start_time: value };
      if (path === 'operation_end_time') return { ...prev, operation_end_time: value };
      if (path === 'restrictedDeliveryDay') return { ...prev, restrictedDeliveryDay: value };
      if (path === 'comboMenuAvailable') return { ...prev, comboMenuAvailable: value };
      if (path === 'comboMenuPrice')
        return { ...prev, comboMenuPrice: sanitizeBoundedDecimal(value, MAX_STORE_PRICE_OR_AMOUNT) };
      if (path === 'comboMenuLabelName') return { ...prev, comboMenuLabelName: value };
      if (path === 'regularMenuAvailable') return { ...prev, regularMenuAvailable: value };
      if (path === 'delivery_type_available') return { ...prev, delivery_type_available: value };
      if (path === 'delivery_kitchen_fee')
        return {
          ...prev,
          delivery_kitchen_fee: sanitizeBoundedDecimal(value, MAX_STORE_PRICE_OR_AMOUNT),
        };
      if (path === 'promo_tracking_code')
        return { ...prev, promo_tracking_code: sanitizeStoreText(value) };
      return prev;
    });
  }, []);

  const updateStoreFrontImageUrl = useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      store_front_images: { ...prev.store_front_images, [key]: value },
    }));
  }, []);

  const setStoreFrontImageFile = useCallback((key, file) => {
    setStoreFrontImageFiles((prev) => ({ ...prev, [key]: file }));
  }, []);

  const setStoreFrontImagePreviewUrl = useCallback((key, url) => {
    setStoreFrontImagePreviewUrls((prev) => {
      if (prev[key]) URL.revokeObjectURL(prev[key]);
      return { ...prev, [key]: url };
    });
  }, []);

  const updateDeliveryAgreement = useCallback((key, value) => {
    let nextValue = value;
    if (key === 'minimumAmountSpend') {
      nextValue = sanitizeBoundedDecimal(value, MAX_DELIVERY_MIN_SPEND);
    }
    if (key === 'percentageToPay_byKitchen') {
      nextValue = sanitizeBoundedDecimal(value, MAX_DELIVERY_KITCHEN_PERCENT);
    }

    setForm((prev) => ({
      ...prev,
      delivery_agreement_fees: { ...prev.delivery_agreement_fees, [key]: nextValue },
    }));
  }, []);

  const updatePickupPromoField = useCallback((key, value) => {
    const nextValue =
      key === 'minimumAmountSpend'
        ? sanitizeBoundedDecimal(value, MAX_STORE_PRICE_OR_AMOUNT)
        : value;

    setForm((prev) => ({
      ...prev,
      pickup_order_promotion: { ...prev.pickup_order_promotion, [key]: nextValue },
    }));
  }, []);

  const updateKeyword = useCallback((index, value) => {
    setForm((prev) => {
      const next = [...prev.search_key_words];
      next[index] = sanitizeStoreText(value);
      return { ...prev, search_key_words: next };
    });
  }, []);

  const addKeyword = useCallback(() => {
    setForm((prev) => {
      if (prev.search_key_words.length >= MAX_SEARCH_KEYWORDS) return prev;
      return { ...prev, search_key_words: [...prev.search_key_words, ''] };
    });
  }, []);

  const removeKeyword = useCallback((index) => {
    setForm((prev) => ({
      ...prev,
      search_key_words: prev.search_key_words.filter((_, i) => i !== index),
    }));
  }, []);

  const toggleFoodFilterCategory = useCallback((value) => {
    setForm((prev) => {
      const has = prev.food_filter_categories.includes(value);
      return {
        ...prev,
        food_filter_categories: has
          ? prev.food_filter_categories.filter((v) => v !== value)
          : [...prev.food_filter_categories, value],
      };
    });
  }, []);

  const toggleReligiousFilterCategory = useCallback((value) => {
    setForm((prev) => {
      const has = prev.religious_filter_categories.includes(value);
      return {
        ...prev,
        religious_filter_categories: has
          ? prev.religious_filter_categories.filter((v) => v !== value)
          : [...prev.religious_filter_categories, value],
      };
    });
  }, []);

  const updateFreeItem = useCallback((index, key, value) => {
    if (index === 0) return;
    setForm((prev) => {
      const items = [...prev.pickup_order_promotion.pickup_free_items];
      items[index] = { ...items[index], [key]: sanitizeStoreText(value) };
      return {
        ...prev,
        pickup_order_promotion: {
          ...prev.pickup_order_promotion,
          pickup_free_items: items,
        },
      };
    });
  }, []);

  const addFreeItem = useCallback(() => {
    setForm((prev) => {
      const items = prev.pickup_order_promotion.pickup_free_items;
      if (items.length >= MAX_PICKUP_FREE_ITEMS) return prev;
      return {
        ...prev,
        pickup_order_promotion: {
          ...prev.pickup_order_promotion,
          pickup_free_items: [...items, { name: '', size: '' }],
        },
      };
    });
  }, []);

  const removeFreeItem = useCallback((index) => {
    if (index === 0) return;
    setForm((prev) => ({
      ...prev,
      pickup_order_promotion: {
        ...prev.pickup_order_promotion,
        pickup_free_items: prev.pickup_order_promotion.pickup_free_items.filter(
          (_, i) => i !== index
        ),
      },
    }));
  }, []);

  const tabFooterProps = {
    form,
    baseline,
    saveAcknowledged,
    setSaveAcknowledged,
    kitchenUpdateAPIResponse,
    uploadImage,
    handleTabUpdate,
    storeImageFile,
    storeFrontImageFiles,
  };

  return {
    form,
    baseline,
    loadInFlight,
    allowLayout,
    layoutMaxWidth: MAX_WIDTH,
    sectionMeta,
    selectedId,
    setSelectedId,
    storeImageFile,
    setStoreImageFile,
    storeImagePreviewUrl,
    setStoreImagePreviewUrl,
    storeFrontImageFiles,
    setStoreFrontImageFile,
    storeFrontImagePreviewUrls,
    setStoreFrontImagePreviewUrl,
    updateField,
    updateStoreFrontImageUrl,
    updateDeliveryAgreement,
    updatePickupPromoField,
    updateKeyword,
    addKeyword,
    removeKeyword,
    toggleFoodFilterCategory,
    toggleReligiousFilterCategory,
    updateFreeItem,
    addFreeItem,
    removeFreeItem,
    tabFooterProps,
  };
}
